import { useMemo } from 'react';

import {
  applyDateRangeFilter,
  applyRangeFilter,
  applySearch,
  applySelectFilter,
  applySort,
  applyTextMatchFilter
} from './list-filter-utils';
import { DateRangeFilter, RangeFilter, SelectFilter, SortConfig, TextMatchFilter } from './list-filter-types';

interface RangeFilterStep<T> {
  type: 'range';
  accessor: (item: T) => number | undefined;
  filter: RangeFilter | undefined;
}

interface DateRangeFilterStep<T> {
  type: 'dateRange';
  accessor: (item: T) => Date | undefined;
  filter: DateRangeFilter | undefined;
}

interface TextMatchFilterStep<T> {
  type: 'textMatch';
  accessor: (item: T) => string;
  filter: TextMatchFilter | undefined;
}

interface SelectFilterStep<T> {
  type: 'select';
  accessor: (item: T) => string;
  filter: SelectFilter | undefined;
}

interface CustomFilterStep<T> {
  type: 'custom';
  apply: (items: T[]) => T[];
}

export type FilterStep<T> =
  | RangeFilterStep<T>
  | DateRangeFilterStep<T>
  | TextMatchFilterStep<T>
  | SelectFilterStep<T>
  | CustomFilterStep<T>;

interface UseFilteredDataArgs<T> {
  /** Full unfiltered data set */
  allData: T[];
  /** Free-text search term */
  searchTerm: string;
  /** Accessor functions for search matching */
  searchAccessors: ((item: T) => string)[];
  /** Declarative filter pipeline — each step is applied in order */
  filterSteps: FilterStep<T>[];
  /** Current sort configuration */
  sortConfig: SortConfig | undefined;
  /** Column comparators for sorting */
  comparators: Record<string, (a: T, b: T) => number>;
}

/**
 * Generic hook that applies a composable filter + sort pipeline to data.
 * Returns the memoised filtered/sorted result.
 *
 * Pipeline order: search → filter steps (in order) → sort
 */
export const useFilteredData = <T>({
  allData,
  searchTerm,
  searchAccessors,
  filterSteps,
  sortConfig,
  comparators
}: UseFilteredDataArgs<T>): T[] => {
  return useMemo(() => {
    let result = allData;

    // Search
    result = applySearch(result, searchAccessors, searchTerm);

    // Filter steps
    for (const step of filterSteps) {
      switch (step.type) {
        case 'range':
          result = applyRangeFilter(result, step.accessor, step.filter);
          break;
        case 'dateRange':
          result = applyDateRangeFilter(result, step.accessor, step.filter);
          break;
        case 'textMatch':
          result = applyTextMatchFilter(result, step.accessor, step.filter);
          break;
        case 'select':
          result = applySelectFilter(result, step.accessor, step.filter);
          break;
        case 'custom':
          result = step.apply(result);
          break;
        default:
          break;
      }
    }

    // Sort
    result = applySort(result, sortConfig, comparators);

    return result;
    // filterSteps is an array built inline by the consumer — we rely on the consumer
    // to include the correct dependencies in their own useMemo/array construction.
  }, [allData, searchTerm, searchAccessors, filterSteps, sortConfig, comparators]);
};
